(() => {
  if (window.RRProductEditorial) return;
  const instances = new Map();
  const cleanLabel = (value) => String(value || '').split(/\s*-\s*ETA\b/i)[0].trim();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ProductPresentation {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.groups = [];
      this.frame = 0;
      const signal = this.abort.signal;
      this.initTabs();
      this.initUnits();
      this.initOptions();
      this.syncColor();
      this.root.addEventListener('change', () => {
        this.syncColor();
        this.groups.forEach((group) => this.updateOptions(group));
        this.scheduleMeasure();
      }, { signal });
      window.addEventListener('scroll', () => this.scheduleMeasure(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal });
      window.addEventListener('load', () => { this.attachGallery(); this.scheduleMeasure(); }, { signal });
      this.resize = new ResizeObserver(() => this.scheduleMeasure());
      const info = root.querySelector('.rr-product__info-inner');
      if (info) this.resize.observe(info);
      this.header = document.querySelector('.o-header-group');
      if (this.header) this.resize.observe(this.header);
      this.gallery = root.querySelector('.js-product-media-items');
      if (this.gallery) {
        this.galleryObserver = new MutationObserver(() => this.attachGallery());
        this.galleryObserver.observe(this.gallery, { attributes: true, attributeFilter: ['class'] });
        this.attachGallery();
      }
      this.scheduleMeasure();
    }

    initTabs() {
      this.tabs = [...this.root.querySelectorAll('[data-rr-tab]')];
      this.panels = [...this.root.querySelectorAll('[data-rr-panel]')];
      if (!this.tabs.length) return;
      this.root.querySelector('[data-rr-tab-list]').setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.addEventListener('click', () => this.selectTab(index), { signal: this.abort.signal });
        tab.addEventListener('keydown', (event) => {
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          this.selectTab(next);
          this.tabs[next].focus({ preventScroll: true });
        }, { signal: this.abort.signal });
      });
      this.panels.forEach((panel) => {
        panel.setAttribute('role', 'tabpanel');
        panel.tabIndex = 0;
      });
      this.selectTab(0);
    }

    selectTab(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.id !== this.tabs[index].getAttribute('aria-controls'); });
      const tab = this.tabs[index];
      const list = tab.parentElement;
      // Scroll only the tab strip, keeping the product and selected options in place.
      if (list.scrollWidth > list.clientWidth) {
        list.scrollTo({ left: tab.offsetLeft - list.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2, behavior: reducedMotion() ? 'instant' : 'smooth' });
      }
      this.scheduleMeasure();
    }

    initUnits() {
      const units = this.root.querySelector('[data-rr-units]');
      if (!units) return;
      units.hidden = false;
      const panels = this.root.querySelectorAll('[data-rr-unit-panel]');
      const select = (value) => {
        panels.forEach(panel => { panel.hidden = panel.dataset.rrUnitPanel !== value; });
        units.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.rrUnit === value)));
        this.scheduleMeasure();
      };
      units.addEventListener('click', event => {
        const button = event.target.closest('[data-rr-unit]');
        if (button) select(button.dataset.rrUnit);
      }, { signal: this.abort.signal });
      select('in');
    }

    initOptions() {
      const limit = Number(this.root.dataset.sizeLimit) || 8;
      this.root.querySelectorAll('.js-variants:not(.js-variants-quick-add) .m-variants__fieldset').forEach((fieldset, index) => {
        if (fieldset.querySelector('.rr-product__options')) return;
        const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
        if (!inputs.length) return;
        const grid = document.createElement('div');
        grid.className = 'rr-product__options';
        grid.id = `rr-options-${this.root.dataset.section}-${index}`;
        const labels = inputs.map((input) => input.nextElementSibling);
        if (labels.some((label) => label?.classList.contains('m-variants__label') && label.textContent.trim().length > 8)) {
          grid.classList.add('rr-product__options--long');
        }
        const items = inputs.map((input, i) => {
          const item = document.createElement('span');
          item.className = 'rr-product__option';
          item.append(input, labels[i]);
          grid.append(item);
          return item;
        });
        fieldset.append(grid);
        const group = { fieldset, grid, inputs, items, limit, expanded: false };
        if (inputs.length > limit) {
          group.button = document.createElement('button');
          group.button.type = 'button';
          group.button.className = 'rr-product__option-toggle';
          group.button.setAttribute('aria-controls', grid.id);
          group.button.addEventListener('click', () => {
            const before = grid.getBoundingClientRect().height;
            group.expanded = !group.expanded;
            this.updateOptions(group);
            const after = grid.getBoundingClientRect().height;
            if (!reducedMotion() && before !== after) {
              group.animation?.cancel();
              group.animation = grid.animate([{ height: `${before}px`, overflow: 'hidden' }, { height: `${after}px`, overflow: 'hidden' }], { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
            }
            this.scheduleMeasure();
          }, { signal: this.abort.signal });
          fieldset.append(group.button);
          const legend = fieldset.querySelector('legend');
          if (legend && !legend.querySelector('[data-selected-value]')) {
            group.selected = document.createElement('span');
            group.selected.className = 'rr-product__selected-option';
            legend.append(group.selected);
          }
        }
        this.groups.push(group);
        this.updateOptions(group);
      });
    }

    updateOptions(group) {
      group.items.forEach((item, index) => {
        item.hidden = !group.expanded && index >= group.limit && !group.inputs[index].checked;
      });
      if (group.button) {
        group.button.setAttribute('aria-expanded', String(group.expanded));
        group.button.textContent = group.expanded ? this.root.dataset.lessOptions : `${this.root.dataset.moreOptions} (${group.inputs.length})`;
      }
      if (group.selected) group.selected.textContent = cleanLabel(group.inputs.find((input) => input.checked)?.value);
    }

    syncColor() {
      const color = this.root.querySelector('[data-rr-product-color]');
      if (!color) return;
      const fieldset = [...this.root.querySelectorAll('.js-variants:not(.js-variants-quick-add) .m-variants__fieldset')].find((item) => /colou?r/i.test(item.querySelector('legend, label')?.textContent || ''));
      const value = fieldset?.querySelector('input:checked, select');
      if (value) color.textContent = cleanLabel(value.value);
    }

    attachGallery() {
      const swiper = this.gallery?.swiper;
      if (!swiper || swiper.destroyed || swiper === this.swiper) return;
      this.swiper = swiper;
      const count = this.root.querySelector('[data-rr-gallery-count]');
      const total = this.gallery.querySelectorAll('[data-media-id]').length;
      this.galleryCounter = () => { if (count) count.textContent = `${swiper.realIndex + 1} / ${total}`; };
      swiper.on('realIndexChange', this.galleryCounter);
      this.galleryCounter();
    }

    scheduleMeasure() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        const info = this.root.querySelector('.rr-product__info-inner');
        const rect = this.header?.getBoundingClientRect();
        const visibleHeader = rect && rect.top <= 1 ? Math.max(0, Math.min(rect.bottom, window.innerHeight)) : 0;
        const preferredTop = visibleHeader + 24;
        const contentHeight = info?.getBoundingClientRect().height || 0;
        // A tall option list must be able to scroll far enough to reach its buy button.
        const stickyTop = Math.min(preferredTop, window.innerHeight - contentHeight - 24);
        this.root.style.setProperty('--rr-pdp-header', `${visibleHeader}px`);
        this.root.style.setProperty('--rr-pdp-sticky-top', `${stickyTop}px`);
      });
    }

    destroy() {
      this.abort.abort();
      this.resize.disconnect();
      this.galleryObserver?.disconnect();
      if (this.swiper && !this.swiper.destroyed) this.swiper.off('realIndexChange', this.galleryCounter);
      cancelAnimationFrame(this.frame);
      this.groups.forEach((group) => group.animation?.cancel());
    }
  }

  const init = () => {
    for (const [root, instance] of instances) {
      if (!root.isConnected) { instance.destroy(); instances.delete(root); }
    }
    document.querySelectorAll('[data-rr-product]').forEach((root) => {
      if (!instances.has(root)) instances.set(root, new ProductPresentation(root));
    });
  };
  window.RRProductEditorial = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) {
      if (event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    }
  });
  document.addEventListener('shopify:block:select', (event) => {
    for (const instance of instances.values()) {
      const index = instance.tabs.findIndex((tab) => tab.dataset.rrBlock === event.detail?.blockId);
      if (index >= 0) instance.selectTab(index);
    }
  });
})();
(() => {
  const bind = () => {
    if (!window.WhatSub || window.rrSkuSyncBound) return;
    window.rrSkuSyncBound = true;
    window.WhatSub.subscribe('VARIANT_CHANGE', ({ currentVariant, section }) => {
      const root = document.getElementById(`main-product-${section}`);
      const sku = String(currentVariant?.sku ?? '').trim();
      root?.querySelectorAll('[data-rr-sku-row]').forEach((row) => {
        const value = row.querySelector('[data-rr-sku-value]');
        if (value) value.textContent = sku;
        row.hidden = !sku;
      });
    });
  };
  if (window.WhatSub) bind();
  else window.addEventListener('load', bind, { once: true });
})();